import { get } from 'lodash';
import PropTypes from 'prop-types';
import React, { useCallback } from 'react';
import { useMediaQuery } from 'react-responsive';

import { BACKGROUND_TYPES } from 'constants/homepage';
import { mediumUp } from 'constants/mediaQueries';
import HeroSlideThemeableModel from 'models/HeroSlide/HeroSlideThemeable';
import Rudderstack from 'utils/Rudderstack';
import { cn } from 'utils/Tailwind';

import BackgroundImage from './BackgroundImage';
import BackgroundVideo from './BackgroundVideo';
import { FONTS, H_ALIGN, THEME, THEME_VARIANT, V_ALIGN } from './constants';

const useResponsiveValue = (desktopValue, mobileValue) => {
  const isMediumUp = useMediaQuery({ query: mediumUp });
  return isMediumUp ? desktopValue : mobileValue;
};

const HeroSlideThemeable = ({ data }) => {
  const {
    backgroundAsset,
    backgroundType,
    body,
    ctaLinks,
    disclaimer,
    eyebrow,
    font,
    heading,
    horizontalAlignmentDesktop,
    horizontalAlignmentMobile,
    isInverted,
    showEyebrowDesktop,
    showEyebrowMobile,
    subheading,
    theme,
    themeVariant,
    verticalAlignmentDesktop,
    verticalAlignmentMobile,
  } = data;

  const bgType = useResponsiveValue(backgroundType.landscape, backgroundType.portrait);
  const hAlign = useResponsiveValue(horizontalAlignmentDesktop, horizontalAlignmentMobile);
  const vAlign = useResponsiveValue(verticalAlignmentDesktop, verticalAlignmentMobile);
  const isEyebrowVisible = useResponsiveValue(showEyebrowDesktop, showEyebrowMobile);

  const heroImageName = useResponsiveValue(
    get(data, ['_data', 'heroAssetLandscape', 'title']),
    get(data, ['_data', 'heroAssetPortrait', 'title'])
  );

  const trackCTAClick = useCallback(
    ({ url, text, event }) => {
      event?.preventDefault();
      event?.stopPropagation();

      Rudderstack.navigationDelayForIphoneAndSafari({
        analyticsFn: () => {
          Rudderstack.homepageHeroClick({
            heroImageName,
            linkCTAName: text,
            linkDestination: url,
          });
        },
        urlToNavigate: url,
      });

      return true;
    },
    [heroImageName]
  );

  return (
    <>
      <div data-testid="hero-slide-themeable" className="absolute inset-0">
        {bgType === BACKGROUND_TYPES.VIDEO ? (
          <BackgroundVideo video={backgroundAsset} />
        ) : (
          <BackgroundImage image={backgroundAsset} />
        )}
      </div>

      <div
        data-testid="hero-content"
        className={cn(
          'sm:p-18 absolute inset-0 flex flex-col p-6 max-md:pt-12 md:p-[calc(1vw+1rem)] lg:p-[calc(1.5vw+1rem)]',
          [vAlign === V_ALIGN.CENTER && 'justify-center']
        )}
      >
        <div
          data-testid="hero-lockup"
          className={cn(
            'flex flex-1 flex-col pb-6 lg:pb-10',
            [vAlign === V_ALIGN.CENTER && 'justify-center'],
            [
              vAlign === V_ALIGN.CENTER &&
                hAlign === H_ALIGN.CENTER &&
                theme === THEME.PROMO &&
                'items-center justify-end md:flex-initial',
            ],
            [vAlign === V_ALIGN.TOP && theme === THEME.PROMO && 'md:flex-initial'],
            [vAlign === V_ALIGN.TOP && 'justify-start'],
            [vAlign === V_ALIGN.CENTER && 'justify-center'],
            [vAlign === V_ALIGN.BOTTOM && 'justify-end'],
            [vAlign === V_ALIGN.STRETCH && 'justify-start'],
            [hAlign === H_ALIGN.LEFT && 'items-start self-start'],
            [hAlign === H_ALIGN.CENTER && 'items-center self-center text-center'],
            [hAlign === H_ALIGN.RIGHT && 'items-end self-end text-right'],
            [isInverted ? 'text-white' : 'text-black']
          )}
        >
          {eyebrow && (
            <p
              data-testid="hero-eyebrow"
              className={cn(
                'font-sans uppercase tracking-wider capsize',
                [theme === THEME.BAU && 'mb-4 text-[20px] leading-none'],
                [theme === THEME.PROMO && 'text-2xl leading-none lg:text-[32px] lg:font-medium'],
                [
                  theme === THEME.FLEX &&
                    'leading-none [font-size:clamp(0.875rem,.5vw+1rem,1.5rem)] after:my-6 after:block after:h-0.5 after:w-12 after:rounded-full after:bg-black after:content-["_"] after:lg:my-8 after:lg:w-20',
                ],
                [hAlign === H_ALIGN.CENTER && 'after:place-self-center'],
                [hAlign === H_ALIGN.RIGHT && 'after:place-self-end'],
                [theme === THEME.PROMO && 'mb-6 lg:mb-10'],
                [isInverted && 'after:bg-white'],
                [!eyebrow || (!isEyebrowVisible && 'mb-0 !hidden')]
              )}
            >
              {eyebrow}
            </p>
          )}

          <p
            data-testid="hero-heading"
            className={cn(
              'whitespace-pre-line capsize',
              [font === FONTS.GEOGRAPH && 'font-sans uppercase tracking-widest'],
              [font === FONTS.SELF_MODERN && 'ligatures-none font-serif font-normal'],
              [
                theme === THEME.BAU &&
                  'mb-4 text-2xl font-medium max-md:-mt-2 md:leading-none lg:text-3xl',
              ],
              [
                theme === THEME.PROMO &&
                  'mb-6 text-5xl leading-none tracking-normal sm:text-6xl md:text-7xl lg:mb-10 lg:text-[110px]',
              ],
              [
                theme === THEME.FLEX &&
                  'mb-6 text-5xl font-medium leading-none tracking-tight md:text-6xl lg:mb-8 lg:text-7xl xl:text-8xl',
              ]
            )}
          >
            {heading}
          </p>

          {subheading && (
            <p
              data-testid="hero-subheading"
              className={cn(
                'whitespace-pre-line font-sans uppercase tracking-wider capsize',
                [theme === THEME.BAU && 'mb-6 hidden'],
                [theme === THEME.FLEX && 'mb-6 text-1xl leading-none lg:mb-8 lg:text-[40px]'],
                [theme === THEME.PROMO && 'mb-6 text-[20px] leading-none'],
                [
                  themeVariant === THEME_VARIANT.EMPHASIZED &&
                    'text-2xl font-medium leading-none md:text-[32px]',
                ]
              )}
            >
              {subheading}
            </p>
          )}

          {theme === THEME.PROMO && (
            <div
              data-testid="promo-divider"
              className={cn(
                'mb-5 h-0.5 w-12 rounded-full text-sm leading-none lg:w-20 lg:text-3xl',
                [isInverted && 'bg-white']
              )}
            />
          )}

          {body && (
            <div
              data-testid="hero-body"
              className={cn([
                vAlign === V_ALIGN.STRETCH && 'mt-auto',
                theme === THEME.BAU && 'md:-mt-1',
              ])}
            >
              <p className=" whitespace-pre-line font-sans text-sm uppercase tracking-widest capsize">
                {body}
              </p>
            </div>
          )}

          {disclaimer && (
            <p
              data-testid="hero-disclaimer"
              className="mt-5 font-sans text-[10px] uppercase tracking-widest capsize lg:mt-6"
            >
              {disclaimer}
            </p>
          )}
        </div>

        {ctaLinks?.length > 0 && (
          <div
            data-testid="hero-cta-links"
            className={cn(
              'grid gap-2 lg:gap-4',
              [vAlign === V_ALIGN.CENTER && 'md:justify-start'],
              [hAlign === H_ALIGN.LEFT && 'md:items-start md:self-start'],
              [hAlign === H_ALIGN.CENTER && 'md:items-center md:self-center md:text-center'],
              [hAlign === H_ALIGN.RIGHT && 'md:items-end md:self-end md:text-right'],
              [ctaLinks.length === 1 && 'grid-cols-1'],
              [ctaLinks.length === 2 && 'grid-cols-2']
            )}
          >
            {ctaLinks.slice(0, 2).map(({ url, text }) => (
              <a
                href={url}
                className={cn(
                  'flex-1 whitespace-pre bg-white px-3 py-2.5 text-center text-xs font-medium uppercase tracking-widest text-black transition-colors duration-300 ease-in-out hover:bg-black hover:text-white sm:flex-auto lg:px-6 lg:py-3 lg:text-sm'
                )}
                key={text}
                onClick={event => trackCTAClick({ event, text, url })}
              >
                {text}
              </a>
            ))}
          </div>
        )}
      </div>
    </>
  );
};

HeroSlideThemeable.propTypes = {
  data: PropTypes.instanceOf(HeroSlideThemeableModel),
};

HeroSlideThemeable.defaultProps = {};

export default HeroSlideThemeable;
